import { createFileRoute, Link } from "@tanstack/react-router";
import { canonicalLink, pathFromMatch, routeMeta } from "@/lib/seo";
import { serviceHeadScripts } from "@/lib/serviceJsonLd";
import { ServicePage } from "@/components/ServicePage";
import hero from "@/assets/hog-trap-cage-welded-steel-seguin-tx.jpg";
import heroPicture from "@/assets/hog-trap-cage-welded-steel-seguin-tx.jpg?w=640;1024;1600;1920&format=avif;webp;jpeg&as=picture";
import { HogCageQuoteForm } from "@/components/HogCageQuoteForm";

export const Route = createFileRoute("/hog-traps-and-cages")({
  head: (ctx) => ({
    links: [canonicalLink(pathFromMatch(ctx))],
    meta: routeMeta(ctx, {
      title: "Hog Cages & Hog Traps in Texas — Truck Bed, Trailer & Trap Cages | Seguin, TX",
      description:
        "Custom welded hog cages and hog traps built in Seguin, TX — truck bed hog cages, dog box combos, trailer-mounted cages, catch dog cages, box traps.",
      image: hero,
      imageAlt:
        "Heavy-duty welded steel hog trap cage with corral panels and slide gate in a South Texas ranch at golden hour",
      socialTitle: "Hog Cages & Hog Traps — Welded in Seguin, TX",
      socialDescription:
        "Truck bed hog cages, dog box combos, trailer rigs, corral panels, box traps and drop traps — welded steel or aluminum, built ranch-tough in our Seguin fabrication shop.",
      keywords:
        "hog cage Texas, hog cages, truck bed hog cage, hog trap cage, hog dog catch cage, dog box hog cage combo, corral hog trap, box hog trap, drop hog trap, trailer mounted hog cage, aluminum hog cage, welded steel hog trap, Seguin TX fabrication, South Texas hog trap, Hill Country hog trap, custom hunting rig",
    }),
    scripts: serviceHeadScripts({
      path: "/hog-traps-and-cages",
      serviceName: "Hog Cages & Hog Traps",
      breadcrumbName: "Hog Traps & Cages",
      description:
        "Custom hog cages and hog traps fabricated in Seguin, TX — truck bed cages, trailer rigs, box traps, corral traps, drop traps.",
    }),
  }),
  component: () => (
    <ServicePage
      currentPath="/hog-traps-and-cages"
      serviceName="Hog Cages & Hog Traps"
      breadcrumbs={[
        { label: "Welding & Custom Fabrication", to: "/custom-fabrication" },
        { label: "Custom Dog Boxes & Hunting Trucks", to: "/dog-boxes" },
        { label: "Hog Traps & Cages" },
      ]}
      eyebrow="★ HOG CAGES & HOG TRAPS ★"
      title="Custom Hog Cages, Truck Bed Rigs, Trailer Cages & Trap Systems"
      intro="A hog cage in Texas can mean a lot of things — a welded truck bed rig for hauling live hogs and catch dogs, a trailer-mounted cage for nuisance control work, a box trap or corral trap for population management, or a dog box / hog cage combo built into a hunting truck. We build all of them in our Seguin, TX shop in welded aluminum or steel, sized to your truck, trailer, lease and herd."
      heroImage={heroPicture}
      heroCta={{
        eyebrow: "Welded Aluminum or Steel · Seguin, TX",
        headline: "Spec'ing a Hog Cage or Trap? Get a Free Written Quote.",
        subhead:
          "Tell us the type (truck bed, trailer, box trap, corral, drop trap, or dog box combo), dimensions, gate style, and material — aluminum or steel — and we'll come back same business day.",
        primaryHref: `/#lets-talk?type=${encodeURIComponent("Hog Cage / Hog Trap")}&notes=${encodeURIComponent("Hog cage / hog trap inquiry — please describe type (truck bed, trailer-mounted, box trap, corral panels, drop trap, catch dog cage, or dog box combo), size (LxWxH), gate style (drop / root / saloon / slide), material (aluminum or steel), and how you'll move it.")}`,
        primaryLabel: "Get My Hog Cage Quote",
        phoneNumber: "+1 (830) 390-9588",
      }}
      features={[
        "Truck bed hog cages — full bed, half cage, or dog box combo",
        "Trailer-mounted hog cages with ramps and removable dividers",
        "Box traps, corral panel traps and remote-trigger drop traps",
        "Catch dog cages with insulated roofs and quick-release doors",
        "Aluminum (1x1 or 1x2 tubing) or welded steel construction",
        "Expanded metal vents, lockable storage, water tank mounts, LED lighting",
      ]}
      body={[
        {
          heading: "Truck Bed Hog Cages — Full Bed, Half Cage & Combo Rigs",
          text:
            "The most common hog cage in Texas hunting culture — a welded cage built into the bed of a pickup for hauling live hogs, catch dogs, traps, coolers, fuel and gear. We build them custom to your truck in aluminum or steel.",
          bullets: [
            "Full Bed Cage — covers the entire bed with side access doors, rear swing gate, integrated dog compartments and top rack storage. Built for serious hog hunters and working ranches.",
            "Half Cage / Combo Rig — encloses part of the bed; the rest stays open for feeders, ATVs, coolers or fuel tanks. The everyday Texas ranch truck setup.",
            "Dog Box + Hog Cage Combo — ventilated dog compartments paired with a larger cargo cage. Expanded metal doors, lockable storage, water tank mounts and removable dividers.",
          ],
          cta: { label: "See Custom Dog Boxes & Hunting Trucks", href: "/dog-boxes" },
        },
        {
          heading: "Hog Trap Cages — Box, Corral & Drop Traps",
          text:
            "Trapping systems for capturing wild hogs alive. We weld each style to order from structural steel tubing and welded mesh, sized to your herd and how you plan to move it.",
          bullets: [
            "Box Traps — compact welded cages with spring-loaded doors. Best for 1–3 hogs, beginners, and operations that need an easy-to-move trap.",
            "Corral Traps — large circular or square panel systems built to catch an entire sounder. Pair with remote cellular gates, root-style gates, or saloon gates. The most effective tool for population control.",
            "Drop Traps — suspended frame traps dropped remotely once a full group of hogs gathers underneath. Reduces trap shyness and catches whole sounders in one trigger.",
          ],
        },
        {
          heading: "Trailer-Mounted Hog Cages",
          text:
            "Hog cages welded directly onto a utility trailer for transporting live hogs, running hunting operations across leases, nuisance animal control work, and moving stock around a ranch. Built for daily use on Texas back roads.",
          bullets: [
            "Expanded metal sides for ventilation and visibility",
            "Removable dividers to separate animals or sections",
            "Wash-down floors and drain holes for easy cleanup",
            "Roof shade panels and rear loading ramps",
            "Tie-down points and lockable storage compartments",
          ],
        },
        {
          heading: "Hog Dog Catch Cages",
          text:
            "Smaller containment cages built specifically for catch dogs working hog hunts — Catahoulas, Pit Bulls, Black Mouth Curs, Leopards and similar breeds. Heavy ventilation, insulated roofs, and quick-release doors so you can deploy and recall dogs fast.",
          bullets: [
            "Mounts on truck beds, flatbeds, or side-by-sides",
            "Insulated roof panels to cut Texas heat in the box",
            "Quick-release doors and integrated water systems",
            "Padded floors and divider options for multi-dog rigs",
          ],
          cta: { label: "Browse Dog Box Styles & Options", href: "/dog-boxes" },
        },
        {
          heading: "Aluminum vs. Steel — Which Material Is Right?",
          text:
            "Both materials have a place in Texas hog work. We'll help you pick based on your truck, your budget, and how rough the cage will live.",
          bullets: [
            "Aluminum — most premium custom rigs today. Lighter on the truck, rust resistant, easier on fuel mileage, cleaner appearance. Typically 1x1 or 1x2 tubing with diamond plate tops and expanded metal vents.",
            "Steel — heavier but cheaper and extremely strong. Preferred for traps that live outside year-round, ranch abuse, and rough terrain. Best paired with hot-dip galvanizing for long service life.",
          ],
        },
        {
          heading: "Popular Features Texas Hunters & Ranchers Ask For",
          text:
            "Most of our hog cage builds bundle several of these features. Tell us which ones matter most and we'll spec the build around them.",
          bullets: [
            "Lockable storage compartments and gun drawers",
            "Expanded metal breather doors and adjustable vent sliders",
            "Removable dividers for multi-animal or multi-dog hauling",
            "Powder coating or hot-dip galvanizing in factory or camo finishes",
            "Water tank mounts and integrated dog watering systems",
            "LED lighting packages and roof racks",
            "Side access doors, slide-out trays and drain floors",
            "Insulated dog compartments for hot Texas afternoons",
          ],
        },
        {
          heading: "Complete Hunting Package Builds",
          text:
            "More Seguin and South Texas customers are moving away from standalone cages and ordering complete hunting truck packages — a single build that integrates everything they need. We can spec and weld the full rig in one shop, on one truck.",
          bullets: [
            "Dog box + hog cage combination",
            "Lockable storage, gun rack and electronics compartment",
            "Water system, cooler slide, and fuel tank mounting",
            "Headache rack, roof rack and LED lighting package",
            "Camo or color-matched powder coat finish",
          ],
          cta: { label: "See Custom Welding & Fabrication", href: "/custom-fabrication" },
        },
      ]}
      applicationsEyebrow="WHO WE BUILD FOR"
      applicationsHeading="Built For Texas Hunters, Ranchers & Trappers"
      applications={[
        "Hog Hunters & Catch Dog Crews",
        "Hill Country Ranches",
        "South Texas Brush Country",
        "Working Cattle Ranches",
        "Hunting Outfitters",
        "Wildlife & Nuisance Animal Pros",
      ]}
      faqs={[
        {
          q: "What's the difference between a hog cage and a hog trap?",
          a: "A hog cage is for transporting or containing live hogs (and often catch dogs) on a truck bed, trailer or rig. A hog trap is built to catch wild hogs in the field — box traps, corral panel traps, or drop traps. A lot of Texas hunters end up needing both: a trap to catch and a cage to haul.",
        },
        {
          q: "Should I go with aluminum or steel?",
          a: "Aluminum is lighter, rust resistant, easier on fuel, and looks cleaner on a nice truck — most premium custom rigs use aluminum 1x1 or 1x2 tubing with diamond plate tops. Steel is heavier but cheaper and extremely strong, which is why we still recommend it for traps that live outside, rough ranch use, and high-abuse builds. We can quote both side by side.",
        },
        {
          q: "Can you build a dog box and a hog cage as one unit?",
          a: "Yes — the dog box + hog cage combo is one of our most-requested builds. Ventilated dog compartments with expanded metal doors on one section, a larger cargo cage on the other, and shared features like lockable storage, water tank mounts and removable dividers.",
        },
        {
          q: "What kind of trap works best for whole sounders?",
          a: "Corral traps and drop traps are the most effective for catching an entire sounder. Corral traps use modular panels with a funnel entry and a remote, root, or saloon gate. Drop traps suspend a welded frame above the bait site and drop it remotely once the whole group is inside, which cuts down on trap shyness.",
        },
        {
          q: "Are the trap and cage builds portable?",
          a: "Most are designed to be moved. Box traps and corral panels can be skid-mounted, trailer-mounted, or pinned together for one or two people to move with a side-by-side or tractor. Trailer-mounted hog cages are built on utility trailers so you can hook up and haul anywhere on the lease.",
        },
        {
          q: "What finish holds up best in Texas weather?",
          a: "Hot-dip galvanizing is the most durable for steel traps and cages that live outside year-round. Powder coat — including camo and color-matched finishes — works well for truck bed cages, trailer rigs, and any build that's kept on a trailer or under cover. Aluminum can be left raw or polished.",
        },
        {
          q: "Do you build full hunting truck packages?",
          a: "Yes. We can build a complete package — dog box, hog cage, storage, gun rack, water system, cooler slide, headache rack, LED lighting and finish — all on one truck, in one Seguin shop. That's where most of our hog hunting customers end up.",
        },
      ]}
    >
      <section className="bg-cream py-16">
        <div className="mx-auto max-w-5xl px-4 lg:px-8">
          <div className="font-display text-xs uppercase tracking-[0.3em] text-red">★ Related Services</div>
          <h2 className="mt-2 font-display text-3xl font-bold text-navy-deep md:text-4xl">
            Pair Your Hog Cage With the Rest of the Rig
          </h2>
          <p className="mt-3 max-w-3xl text-muted-foreground">
            Most Texas hog hunters and ranchers don't stop at a cage. Browse the related builds we
            weld in the same Seguin shop and bundle into one turn-key hunting truck.
          </p>
          <div className="mt-8 grid gap-6 md:grid-cols-3">
            <Link
              to="/dog-boxes"
              className="group block rounded-2xl border border-navy-deep/10 bg-white p-6 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
            >
              <h3 className="font-display text-xl font-bold text-navy-deep group-hover:text-red">
                Custom Dog Boxes & Hunting Truck Builds →
              </h3>
              <p className="mt-2 text-sm text-muted-foreground">
                Insulated aluminum dog boxes, catch dog compartments and full hunting truck rigs that
                pair directly with a truck bed hog cage or dog box / hog cage combo.
              </p>
            </Link>
            <Link
              to="/custom-fabrication"
              className="group block rounded-2xl border border-navy-deep/10 bg-white p-6 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
            >
              <h3 className="font-display text-xl font-bold text-navy-deep group-hover:text-red">
                Custom Welding & Fabrication in Seguin, TX →
              </h3>
              <p className="mt-2 text-sm text-muted-foreground">
                Headache racks, gun racks, cooler slides, water tank mounts, LED lighting and
                powder-coat finishes — the welded extras that turn a hog cage into a full hunting
                package.
              </p>
            </Link>
            <Link
              to="/deer-blinds-and-hunting"
              className="group block rounded-2xl border border-navy-deep/10 bg-white p-6 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
            >
              <h3 className="font-display text-xl font-bold text-navy-deep group-hover:text-red">
                Deer Blinds & Hunting Structures →
              </h3>
              <p className="mt-2 text-sm text-muted-foreground">
                Welded tower blinds, skid-mounted blinds, feeder pens and protein cages for the same
                ranches and leases our hog cages and traps ship to.
              </p>
            </Link>
          </div>
        </div>
      </section>

      <section className="bg-white py-16">
        <div className="mx-auto max-w-3xl px-4 lg:px-8">
          <div className="font-display text-xs uppercase tracking-[0.3em] text-red">★ Request a Quote</div>
          <h2 className="mt-2 font-display text-3xl font-bold text-navy-deep md:text-4xl">
            Tell Us About Your Hog Cage or Trap
          </h2>
          <p className="mt-3 text-muted-foreground">
            Share the type (truck bed, trailer, box trap, corral, drop trap, catch dog cage, or dog box combo),
            dimensions, gate style and material — we'll come back with a written estimate.{" "}
            <Link to="/custom-fabrication" className="font-semibold text-navy-deep underline">
              Or see our full welding & fabrication lineup →
            </Link>
          </p>
          <div className="mt-8">
            <HogCageQuoteForm />
          </div>
        </div>
      </section>
    </ServicePage>
  ),
});
